/**
 * Cadre des ecrans hors application (connexion, mot de passe impose) : a
 * gauche sur grand ecran ce que l'application apporte, a droite le formulaire.
 * Sur mobile, seul le formulaire reste, sous une marque compacte.
 */
import type { ReactNode } from 'react';
import { CalendarClock, Handshake, Radar } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { BASELINE_APPLICATION, NOM_APPLICATION, SIGLE_APPLICATION } from '../lib/marque';

const ATOUTS: ReadonlyArray<{ icone: LucideIcon; texte: string }> = [
  { icone: Radar, texte: 'Des entreprises ciblées, issues des registres officiels.' },
  { icone: CalendarClock, texte: 'Des relances qui ne passent plus à la trappe.' },
  { icone: Handshake, texte: 'Un suivi clair, du premier appel à la signature.' },
];

function PastilleMarque({ className }: { className: string }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${className}`}
    >
      {SIGLE_APPLICATION}
    </span>
  );
}

export function CadreAuthentification({
  titre,
  sousTitre,
  children,
  pied,
}: {
  titre: string;
  sousTitre: string;
  children: ReactNode;
  pied?: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-fond lg:grid lg:grid-cols-2">
      <section
        aria-labelledby="proposition-valeur"
        className="hidden flex-col justify-between bg-linear-to-br from-accent-600 to-accent-900 p-12 text-encre-inverse lg:flex"
      >
        <div className="flex items-center gap-3">
          <PastilleMarque className="bg-encre-inverse/15 text-encre-inverse" />
          <span className="text-md font-semibold">{NOM_APPLICATION}</span>
        </div>
        <div className="max-w-md">
          <p id="proposition-valeur" className="text-3xl font-semibold tracking-titre">
            Trouvez vos prochains clients, suivez chaque échange.
          </p>
          <ul className="mt-8 space-y-4">
            {ATOUTS.map(({ icone: Icone, texte }) => (
              <li key={texte} className="flex items-start gap-3 text-md text-accent-100">
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-encre-inverse/10 text-encre-inverse"
                >
                  <Icone />
                </span>
                <span className="pt-1">{texte}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-sm text-accent-100">{BASELINE_APPLICATION}</p>
      </section>

      <main className="flex min-h-screen items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <PastilleMarque className="bg-accent-600 text-encre-inverse shadow-releve" />
            <div>
              <p className="text-md font-semibold text-encre">{NOM_APPLICATION}</p>
              <p className="text-sm text-encre-discrete">{BASELINE_APPLICATION}</p>
            </div>
          </div>

          <h1 className="text-titre-page font-semibold tracking-titre text-encre">{titre}</h1>
          <p className="mt-1 text-sm text-encre-discrete">{sousTitre}</p>
          <div className="mt-6">{children}</div>
          {pied}
        </div>
      </main>
    </div>
  );
}
